import { Box, Button, HStack, Link, Text } from "@chakra-ui/react";
import { CreditFacilitySetupFooter } from "features/credit-facility/setup/CreditFacilitySetupFooter";
import {
  useCreditFacilityLoanListSuspense,
  useCreditFacilityLoansCreate,
} from "gen/endpoints/credit-facility-loan/credit-facility-loan";
import { useTransactionCalculationListSuspense } from "gen/endpoints/transactions/transactions";
import { CreditFacilityLoanData } from "gen/model/creditFacilityLoanData";
import { DayCountEnum } from "gen/model/dayCountEnum";
import { InterestAccrualGranularityLevelEnum } from "gen/model/interestAccrualGranularityLevelEnum";
import { TransactionCalculationData } from "gen/model/transactionCalculationData";
import React from "react";
import { DefaultValues, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormBooleanToggleButtonGroup } from "shared/components/button/ToggleButton";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
} from "shared/components/content/ContentBlock";
import Form from "shared/components/form/Form";
import { FormFieldPrefix } from "shared/components/form/FormFieldPrefix";
import { FormFieldset } from "shared/components/form/FormFieldset";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormMoneyInput } from "shared/components/input/text/MoneyInput";
import { FormNumberInput } from "shared/components/input/text/NumberInput";
import { FormTextInput } from "shared/components/input/text/TextInput";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { usePageWizard } from "shared/components/layout/PageWizard";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";

// FIXME: Update backend model to match
interface FormParams {
  loans: CreditFacilityLoanData[];
}

export const CreditFacilitySetupLoan: React.FC = () => {
  const { t } = useTranslation(["creditFacility", "common"]);

  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();

  const { data: loansData } = useCreditFacilityLoanListSuspense(
    fundFamilyId,
    creditFacilityId,
  );

  const { mutateAsync: creditFacilityBulkCreate } =
    useCreditFacilityLoansCreate();

  const { navigateNext } = usePageWizard();

  const form = useForm<FormParams>({
    defaultValues: {
      loans: loansData.length
        ? loansData
        : [
            {
              credit_facility_id: creditFacilityId,
              fund_family_id: fundFamilyId,
            },
          ],
    } as DefaultValues<FormParams>,
  });

  const { data: calculations } =
    useTransactionCalculationListSuspense(fundFamilyId);

  const {
    fields: loans,
    append: addLoan,
    remove: removeLoan,
  } = useFieldArray({ name: "loans", control: form.control });
  return (
    <PageContent>
      <PageHeader
        workflowName={t("creditFacility:setup.title")}
        title={t("creditFacility:setup.loan.title")}
      />
      <PageBody>
        <Form
          form={form}
          onSubmit={async (data) => {
            await creditFacilityBulkCreate({
              fundFamilyId,
              creditFacilityId,
              data: {
                loans: data.loans.map((loan) => ({
                  ...loan,
                  fund_family_id: fundFamilyId,
                })),
              },
            });
          }}
          onSuccess={navigateNext}
        >
          <ContentBlockGroup>
            <ContentBlock>
              <ContentBlockBody>
                {loans.map((loan, index) => (
                  <FormFieldPrefix key={loan.id} prefix={`loans.${index}`}>
                    <LoanFields
                      loan={loan}
                      index={index}
                      calculations={calculations}
                      onClickDelete={() => removeLoan(index)}
                    />
                  </FormFieldPrefix>
                ))}
                <Button
                  colorScheme="secondary"
                  size="sm"
                  onClick={() =>
                    addLoan({
                      fund_family_id: fundFamilyId,
                      credit_facility_id: creditFacilityId,
                    } as never)
                  }
                >
                  {t("creditFacility:setup.loan.addAnotherLoanType")}
                </Button>
              </ContentBlockBody>
            </ContentBlock>
          </ContentBlockGroup>
          <CreditFacilitySetupFooter />
        </Form>
      </PageBody>
    </PageContent>
  );
};

interface LoanFieldsProps {
  loan: CreditFacilityLoanData;
  index: number;
  onClickDelete: () => void;
  calculations: TransactionCalculationData[];
}

const LoanFields: React.FC<LoanFieldsProps> = ({
  onClickDelete,
  calculations,
}) => {
  const { t } = useTranslation(["creditFacility", "enums"]);
  return (
    <FormFieldset>
      <HStack spacing="40px" alignItems="flex-start">
        <Box flex={1}>
          <Text textStyle="overline" mb={2}>
            {t("creditFacility:setup.loan.rate")}
          </Text>
          <FormSelect
            name="interest_rate_calculation_id"
            title={t("creditFacility:borrowingBase.calculation")}
          >
            {calculations.map(({ id, name }) => (
              <SelectOption key={id} value={id}>
                {name}
              </SelectOption>
            ))}
          </FormSelect>
          <FormTextInput
            name="name"
            title={t("creditFacility:setup.loan.loanName")}
          />
          <FormNumberInput
            name="spread"
            title={t("creditFacility:setup.loan.spread")}
            valueType="percent"
            allowNegative={true}
            decimalScale={4}
            unsetWhenEmpty={true}
          />
          <FormNumberInput
            name="applicable_margin"
            title={t("creditFacility:setup.loan.applicableMarginOptional")}
            valueType="percent"
            decimalScale={4}
            allowNegative={true}
            unsetWhenEmpty={true}
          />
          <FormNumberInput
            name="interest_rate_floor"
            title={t("creditFacility:setup.loan.interestRateFloorOptional")}
            valueType="percent"
            allowNegative={true}
            decimalScale={4}
            unsetWhenEmpty={true}
          />
          <FormNumberInput
            name="interest_rate_ceiling"
            title={t("creditFacility:setup.loan.interestRateCeilingOptional")}
            valueType="percent"
            allowNegative={true}
            decimalScale={4}
            unsetWhenEmpty={true}
          />
          <FormNumberInput
            title={t("creditFacility:setup.loan.referenceRateLookbackPeriod")}
            name="reference_rate_lookback_period"
          />
          <FormSelect
            name="rate_day_count"
            title={t("creditFacility:setup.loan.rateDayCount.title")}
          >
            {Object.values(DayCountEnum).map((value) => (
              <SelectOption key={value} value={value}>
                {t(`enums:DayCountEnum.${value}`)}
              </SelectOption>
            ))}
          </FormSelect>
          <FormSelect
            name="interest_accrual_granularity"
            title={t(
              "creditFacility:setup.loan.interestRateGranularity.interestRateGranularityQ",
            )}
          >
            {Object.values(InterestAccrualGranularityLevelEnum).map((value) => (
              <SelectOption key={value} value={value}>
                {t(
                  `creditFacility:setup.loan.interestAccrualGranularity.options.${value}`,
                )}
              </SelectOption>
            ))}
          </FormSelect>

          <Text textStyle="overline" mb={2}>
            {t("creditFacility:setup.loan.otherDetails")}
          </Text>
          <FormMoneyInput
            name="loan_draw_and_paydown_increments"
            title={t("creditFacility:setup.loan.loanDrawAndPaydownIncrements")}
          />
        </Box>
        <Box flex={1}>
          <Text textStyle="overline" mb={2}>
            {t("creditFacility:setup.loan.drawdown")}
          </Text>
          <FormMoneyInput
            name="minimum_draw_amount"
            title={t("creditFacility:setup.loan.minimumDrawdownAmount")}
          />
          <FormBooleanToggleButtonGroup
            name="charge_interest_on_drawdown_day"
            mb={2}
            title={t("creditFacility:setup.loan.chargeInterestOnDrawdown")}
          />
          <Text textStyle="overline" mb={2}>
            {t("creditFacility:setup.loan.paydown")}
          </Text>
          <FormMoneyInput
            name="minimum_paydown_amount"
            title={t("creditFacility:setup.loan.minimumPaydownAmount")}
          />
          <FormBooleanToggleButtonGroup
            name="charge_interest_on_paydown_day"
            mb={2}
            title={t("creditFacility:setup.loan.chargeInterestOnPaydown")}
          />
        </Box>
      </HStack>
      <HStack justifyContent="flex-end">
        <Link size="xs" colorScheme="gray" onClick={onClickDelete}>
          {t("creditFacility:setup.loan.deleteLoanType")}
        </Link>
      </HStack>
    </FormFieldset>
  );
};
